import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      // Check if we have valid data - must be non-null, non-undefined, and not empty string
      const hasValidToken = token && token !== 'null' && token !== 'undefined' && token.trim() !== '';
      const hasValidUser = savedUser && 
                          savedUser !== 'null' && 
                          savedUser !== 'undefined' && 
                          savedUser.trim() !== '' &&
                          savedUser !== '{}';
      
      if (hasValidToken && hasValidUser) {
        try {
          // Safely parse JSON - handle invalid JSON
          const parsedUser = JSON.parse(savedUser);
          if (parsedUser && typeof parsedUser === 'object' && parsedUser !== null && !Array.isArray(parsedUser)) {
            setUser(parsedUser);
          } else {
            // Invalid user data, clear it
            console.warn('Invalid user data in localStorage, clearing...');
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            setLoading(false);
            return;
          }
        } catch (parseError) {
          console.error('Error parsing saved user:', parseError);
          console.error('Saved user value:', savedUser);
          // Invalid JSON, clear corrupted data
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setLoading(false);
          return;
        }
        
        // Verify token is still valid
        api.get('/me')
          .then((response) => {
            if (response && response.data) {
              setUser(response.data);
              localStorage.setItem('user', JSON.stringify(response.data));
            }
          })
          .catch((error) => {
            console.error('Error verifying token:', error);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          })
          .finally(() => setLoading(false));
      } else {
        // No token or user, clear any corrupted data
        if (savedUser === 'null' || savedUser === 'undefined' || savedUser === '{}' || (savedUser && savedUser.trim() === '')) {
          localStorage.removeItem('user');
        }
        if (token === 'null' || token === 'undefined' || (token && token.trim() === '')) {
          localStorage.removeItem('token');
        }
        setLoading(false);
      }
    } catch (error) {
      console.error('Error in AuthContext useEffect:', error);
      // Clear all auth data on any error
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/login', { email, password });
      const { user, token } = response.data;
      
      if (token && user) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        return user;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    api.post('/logout').finally(() => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    });
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isFounder: user?.role === 'founder',
    isOwner: user?.role === 'owner',
    isOrganizer: user?.role === 'organizer',
    isMedia: user?.role === 'media',
    isPlaceOwner: user?.role === 'place_owner',
    isSponsor: user?.role === 'sponsor',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};






